import * as React from 'react';

import { Box, Card, Flex, Text } from '@opendoor/bricks/core';
import { colors as colorsEero } from '@opendoor/bricks/theme/eero';

import {
  OdProtosListingsData_Listing_State as ListingState,
  OdProtosListingsData_PARN_State as ParnState,
} from '../../__generated__/athenaGlobalTypes';
import { getReadableDate } from '../../helpers/getAllParnLeadData';
import {
  didNotConvertOrCloseStates,
  findStateFromTimestampKey,
  getStateIndex,
  ParnStateToTimestampKey,
  StateToUserStateForParn,
} from './parnUtils';

const Bubble: React.FC = () => {
  const colors = colorsEero;

  let icon: JSX.Element | null = null;
  icon = (
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="
              M9.40464 2.5374
              C9.82278 2.17659 9.86926 1.54514 9.50846 1.127
              C9.14765 0.70886 8.5162 0.662381 8.09806 1.02318
              L3.77812 4.75077
              L2.5191 3.31552
              C2.1549 2.90034 1.52308 2.85901 1.1079 3.22322
              C0.692719 3.58742 0.651394 4.21923 1.0156 4.63441
              L2.79496 6.66284
              C2.82669 6.71862 2.86428 6.77215 2.90779 6.82258
              C3.2686 7.24072 3.90005 7.2872 4.31819 6.92639
              L9.40464 2.5374Z
            "
      fill={colors.neutrals0}
      transform="translate(4, 5)"
    />
  );

  return (
    <svg
      role="img"
      aria-hidden="true"
      display="block"
      viewBox="0 0 18 18"
      width="24px"
      height="24px"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect fill={colors.brand50} x="0" y="0" width="18" height="18" rx="9" />
      {icon}
    </svg>
  );
};

type SpacerProps = {
  isMobile: boolean;
  isLongText: boolean;
};

const Spacer: React.FC<SpacerProps> = ({ isMobile, isLongText }) => {
  return (
    <Box
      gridColumn="span 2"
      borderLeft="3px solid"
      borderColor={'brand50'}
      my="2"
      borderRadius="rounded"
      height={isMobile && isLongText ? '30px' : '15px'}
    />
  );
};

type TaskProps = {
  isLast?: boolean;
  children: React.ReactNode;
  isMobile: boolean;
  isLongText: boolean;
};

const Task: React.FC<TaskProps> = ({ isLast = false, children, isMobile, isLongText }) => {
  return (
    <>
      <Flex alignItems="center" flexDirection="column">
        <Bubble />
        {isLast ? null : <Spacer isMobile={isMobile} isLongText={isLongText} />}
      </Flex>
      <Box lineHeight="110">{children}</Box>
    </>
  );
};

type Step = {
  header: string;
};

type StateTimestampPair = {
  key: string | ListingState;
  value: string;
};

function combineAndSortMaps(
  parnTimestampsMap: Map<string, string>,
  listingsTimestampsMap: Map<ListingState, string>,
): StateTimestampPair[] {
  // Convert maps to arrays of [key, value] pairs
  const parnArray = Array.from(parnTimestampsMap.entries()).filter(
    ([key]) => key !== 'AgentNoteType',
  );
  const listingsArray = Array.from(listingsTimestampsMap.entries());

  // The spread operator concatenates the arrays
  const combinedArray = [...parnArray, ...listingsArray];

  //Sort modifies the array in place
  combinedArray.sort((a, b) => {
    //Convert date strings to dates for comparison
    const dateA = new Date(a[1]);
    const dateB = new Date(b[1]);

    //A negative number means item 'a' should be ordered before 'b'
    if (dateA.getTime() < dateB.getTime()) return -1;
    if (dateA.getTime() > dateB.getTime()) return 1;

    // If dates are equal, order by state
    if (dateA.getTime() == dateB.getTime()) {
      const stateA = findStateFromTimestampKey(a[0]);
      const stateB = findStateFromTimestampKey(b[0]);

      const stateIndexA = getStateIndex(stateA as ParnState | ListingState);
      const stateIndexB = getStateIndex(stateB as ParnState | ListingState);

      if (stateIndexA < stateIndexB) return -1;
      if (stateIndexB < stateIndexA) return 1;
    }
    return 0;
  });
  return combinedArray.map(([key, value]) => ({ key, value }));
}

export function buildTimeline(
  combinedMap: { key: string | ListingState; value: string }[],
  parnState: ParnState,
) {
  const TimelineSteps: Step[] = [];

  TimelineSteps.push({
    header: 'New lead',
  });

  combinedMap.forEach((item) => {
    let headerState = '';
    const displayDate = getReadableDate(item.value);
    //Find the Parn state for that value
    const foundParnState = Object.keys(
      ParnStateToTimestampKey as Array<keyof typeof ParnStateToTimestampKey>,
    ).find(
      (key) => ParnStateToTimestampKey[key as keyof typeof ParnStateToTimestampKey] === item.key,
    );
    //Use user-facing value of ParnState if found
    if (foundParnState && foundParnState in StateToUserStateForParn) {
      headerState = StateToUserStateForParn[foundParnState as keyof typeof StateToUserStateForParn];
    }
    //Check if key is a valid ListingState
    else if (item.key in StateToUserStateForParn) {
      headerState = StateToUserStateForParn[item.key as keyof typeof StateToUserStateForParn];
    }

    const newStep: Step = {
      header: `${headerState} on ${displayDate}`,
    };
    if (headerState !== '') {
      TimelineSteps.push(newStep);
    }
  });

  if (didNotConvertOrCloseStates.slice(0, 4).includes(parnState)) {
    TimelineSteps.push({
      header: 'Did not convert',
    });
  }

  if (didNotConvertOrCloseStates.slice(4, 7).includes(parnState)) {
    TimelineSteps.push({
      header: 'Did not close',
    });
  }

  return TimelineSteps;
}

interface ParnTimelineProps {
  parnTimestampsMap: Map<string, string>;
  listingsTimestampsMap: Map<ListingState, string>;
  parnState?: ParnState;
  isMobile: boolean;
}

export const ParnTimeline: React.FC<ParnTimelineProps> = ({
  parnTimestampsMap,
  listingsTimestampsMap,
  parnState = ParnState.UNKNOWN,
  isMobile,
}) => {
  const combinedMap = combineAndSortMaps(parnTimestampsMap, listingsTimestampsMap);
  const steps = buildTimeline(combinedMap, parnState);

  const timelineStep = steps.map((step, idx) => {
    //Long text gets additional spacing between steps on mobile
    const isLongText = step.header.length > 35;

    return (
      <Task key={idx} isLast={idx + 1 === steps.length} isMobile={isMobile} isLongText={isLongText}>
        <Text size="110" fontWeight="medium">
          {step.header}
        </Text>
        <Flex alignItems="center">
          <Box
            height="24px"
            width="196px"
            borderRadius="semiRounded"
            overflow="hidden"
            display={['none', null, 'block']}
          ></Box>
        </Flex>
      </Task>
    );
  });

  return (
    <Card
      variant="secondary"
      boxShadow="unset"
      padding={isMobile ? '0px 0px 35px 0px' : '25px 0px 35px 0px'}
    >
      <Text
        size={isMobile ? 's3' : 's4'}
        fontWeight={'semibold'}
        paddingTop={'40px'}
        paddingBottom={'12px'}
      >
        Timeline
      </Text>
      <Flex>
        <Box m="10px 0px 60px" display="grid" gridTemplateColumns="24px 1fr" gridGap="0 24px">
          {timelineStep}
        </Box>
      </Flex>
    </Card>
  );
};

export default ParnTimeline;
